import type { Sessao } from './auth';
import { CABECALHO_CSRF } from './auth';
import type { Falha, Resultado } from './acesso';
import { buscar } from './buscar';
import { pedirLancados } from './lancados-no-periodo';
import type { LancadosNoPeriodo } from './lancados-no-periodo';

/**
 * BLOCO 5 — o front de /api/pre-vencidos, /api/lotes e /api/validades (telas 25 e 26).
 * Datas trafegam como 'AAAA-MM-DD'; a tela mostra '05/10/2026'.
 */

export type EstadoDoLote =
  | { readonly estado: 'esgotado' }
  | { readonly estado: 'sem_validade' }
  | { readonly estado: 'vencido'; readonly dias: number }
  | { readonly estado: 'vencido_no_mes'; readonly dias: number }
  | { readonly estado: 'vence_em'; readonly dias: number }
  | { readonly estado: 'fora_da_janela'; readonly dias: number };

export type Lote = {
  readonly id: string;
  readonly codigo: string | null;
  readonly validade: string | null;
  readonly quantidade: string | null;
  readonly origem: 'manual' | 'relatorio' | 'migracao_1_0';
  readonly notaEntrada: string | null;
  readonly dataEntrada: string | null;
  readonly ativo: boolean;
  readonly motivoInativo: string | null;
  readonly criadoPor: string | null;
  readonly criadoEm: string;
  readonly atualizadoEm: string;
  readonly versao: number;
  readonly estado: EstadoDoLote;
};

export type EventoDoLote = {
  readonly tipo: string;
  readonly antes: Record<string, unknown> | null;
  readonly depois: Record<string, unknown> | null;
  readonly motivo: string | null;
  readonly ator: string | null;
  readonly origem: string;
  readonly criadoEm: string;
};

export type LinhaDoCatalogo = {
  /** Unidades lançadas no pré-vencido no período do filtro (só quando a tela manda o período). */
  readonly lancadosNoPeriodo?: number;
  readonly productId: string;
  readonly descricao: string;
  readonly codigo: string | null;
  readonly fabricante: string | null;
  readonly estoqueTotal: string | null;
  readonly lotesAtivos: number;
  readonly loteMaisUrgente: Lote;
};

type Faixa = { readonly lotes: number; readonly unidades: string };

export type Catalogo = {
  readonly itens: readonly LinhaDoCatalogo[];
  readonly total: number;
  readonly resumo: {
    readonly produtos: number;
    readonly lotes: number;
    readonly vencidos: Faixa;
    readonly em7: Faixa;
    readonly em30: Faixa;
    readonly em90: Faixa;
    readonly semValidade: number;
    readonly valorACustoNaJanela: string | null;
    readonly limiteDaJanela: string;
  };
  readonly fabricantes: readonly string[];
  readonly pagina: number;
  readonly tamanhoDaPagina: number;
};

export type ProdutoComLotes = {
  readonly productId: string;
  readonly descricao: string;
  readonly codigo: string | null;
  readonly ean: string | null;
  readonly fabricante: string | null;
  readonly grupo: string | null;
  readonly estoqueTotal: string | null;
  readonly estoqueAtualizadoEm: string | null;
  readonly venderPor: string | null;
  readonly pmc: string | null;
  readonly premioBase: string | null;
  readonly lotes: readonly (Lote & { readonly eventos: readonly EventoDoLote[] })[];
  readonly somaDosLotes: string | null;
};

export type FaixaDeVencimento =
  'vencidos' | 'hoje' | 'ate7' | 'de8a30' | 'de31a60' | 'de61a90' | 'sem_validade';

export type Calendario = {
  readonly faixas: Record<FaixaDeVencimento, Faixa>;
  readonly lotes: readonly {
    readonly loteId: string;
    readonly productId: string;
    readonly descricao: string;
    readonly codigoProduto: string | null;
    readonly codigo: string | null;
    readonly validade: string | null;
    readonly dias: number | null;
    readonly quantidade: string | null;
    readonly valorACusto: string | null;
  }[];
  readonly porDia: readonly { readonly dia: string; readonly lotes: number }[];
  readonly totalAcompanhados: number;
  readonly valorACustoNaJanela: string | null;
  readonly hoje: string;
};

export type Alerta = {
  readonly chave: string;
  readonly tipo: 'vencidos' | 'janela_30' | 'sem_validade' | 'divergencia_validade';
  readonly criticidade: 'critico' | 'atencao' | 'cadastro' | 'dados';
  readonly titulo: string;
  readonly detalhe: string;
  /** Custo dos lotes do alerta — só chega para o dono (sigilo da loja). */
  readonly valorACusto?: string | null;
  readonly lotes: number;
  readonly estado: 'aberto' | 'lido' | 'tratado';
  readonly decididoPor: string | null;
  readonly decididoEm: string | null;
};

export type Alertas = {
  readonly ativos: readonly Alerta[];
  readonly resolvidos: readonly {
    readonly chave: string;
    readonly tipo: Alerta['tipo'];
    readonly ultimoEstado: 'lido' | 'tratado';
    readonly decididoPor: string | null;
    readonly decididoEm: string;
  }[];
};

export type LoteExistente = {
  readonly id: string;
  readonly codigo: string;
  readonly validade: string | null;
  readonly quantidade: string | null;
  readonly origem: Lote['origem'];
  readonly versao: number;
};

export type FalhaDoLote =
  | Falha
  | {
      readonly tipo: 'ja_cadastrado';
      readonly existente: LoteExistente;
      readonly mesmaValidade: boolean;
    };

/** Para mostrar a mensagem: "já cadastrado" vira um conflito comum. */
export function comoFalha(f: FalhaDoLote): Falha {
  return f.tipo === 'ja_cadastrado'
    ? { tipo: 'conflito', mensagem: 'Este lote já está cadastrado.' }
    : f;
}

async function ler<T>(caminho: string): Promise<Resultado<T>> {
  let r: Response;
  try {
    r = await buscar(caminho, { credentials: 'same-origin' });
  } catch {
    return { ok: false, falha: { tipo: 'offline' } };
  }
  if (r.ok) return { ok: true, dados: (await r.json()) as T };
  if (r.status === 401) return { ok: false, falha: { tipo: 'sem-sessao' } };
  if (r.status === 403) return { ok: false, falha: { tipo: 'sem-acesso' } };
  if (r.status === 400) return { ok: false, falha: { tipo: 'invalido', mensagem: '' } };
  return { ok: false, falha: { tipo: 'inesperado', codigo: r.headers.get('x-request-id') } };
}

async function escrever<T>(
  sessao: Sessao,
  caminho: string,
  corpo: unknown,
): Promise<{ ok: true; dados: T } | { ok: false; falha: FalhaDoLote }> {
  let r: Response;
  try {
    r = await fetch(caminho, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', [CABECALHO_CSRF]: sessao.csrfToken },
      body: JSON.stringify(corpo),
    });
  } catch {
    return { ok: false, falha: { tipo: 'offline' } };
  }
  if (r.status === 204) return { ok: true, dados: undefined as T };
  if (r.ok) return { ok: true, dados: (await r.json()) as T };
  let erro: { message?: string; details?: Record<string, unknown> } = {};
  try {
    erro = ((await r.json()) as { error?: typeof erro }).error ?? {};
  } catch {
    // sem corpo
  }
  const mensagem = erro.message ?? '';
  if (r.status === 409 && erro.details?.['existente']) {
    return {
      ok: false,
      falha: {
        tipo: 'ja_cadastrado',
        existente: erro.details['existente'] as LoteExistente,
        mesmaValidade: Boolean(erro.details['mesmaValidade']),
      },
    };
  }
  if (r.status === 409) return { ok: false, falha: { tipo: 'conflito', mensagem } };
  if (r.status === 400) return { ok: false, falha: { tipo: 'invalido', mensagem } };
  if (r.status === 403) return { ok: false, falha: { tipo: 'sem-acesso' } };
  if (r.status === 401) return { ok: false, falha: { tipo: 'sem-sessao' } };
  return { ok: false, falha: { tipo: 'inesperado', codigo: r.headers.get('x-request-id') } };
}

export function lerCatalogo(q: {
  filtro: string;
  busca: string;
  fabricante: string;
  pagina: number;
  lancados?: LancadosNoPeriodo | null | undefined;
}) {
  const p = new URLSearchParams({ filtro: q.filtro, pagina: String(q.pagina) });
  pedirLancados(p, q.lancados);
  if (q.busca.trim()) p.set('busca', q.busca.trim());
  if (q.fabricante) p.set('fabricante', q.fabricante);
  return ler<Catalogo>(`/api/pre-vencidos?${p.toString()}`);
}

export const lerProdutoComLotes = (id: string) => ler<ProdutoComLotes>(`/api/pre-vencidos/${id}`);

export function lerCalendario(q: {
  faixa: FaixaDeVencimento;
  mes: string;
  dia: string | null;
  /** Filtro de período da tela, pela validade: a lista mostra o que vence nele. */
  periodo?: { de: string; ate: string } | null | undefined;
}) {
  const p = new URLSearchParams({ faixa: q.faixa, mes: q.mes });
  if (q.dia) p.set('dia', q.dia);
  if (q.periodo) {
    p.set('de', q.periodo.de);
    p.set('ate', q.periodo.ate);
  }
  return ler<Calendario>(`/api/validades/calendario?${p.toString()}`);
}

export const lerAlertas = () => ler<Alertas>('/api/validades/alertas');

export const lancarLote = (
  sessao: Sessao,
  productId: string,
  corpo: {
    codigo: string;
    validade: string | null;
    quantidade: string;
    notaEntrada?: string | null;
    somarAoExistente?: boolean;
  },
) =>
  escrever<{ loteId: string; somado: boolean; codigo: string }>(
    sessao,
    `/api/pre-vencidos/${productId}/lotes`,
    corpo,
  );

export const corrigirLote = (
  sessao: Sessao,
  loteId: string,
  corpo: { versaoEsperada: number; validade?: string | null; quantidade?: string; motivo: string },
) => escrever<{ versao: number }>(sessao, `/api/lotes/${loteId}/correcao`, corpo);

export const decidirAlerta = (sessao: Sessao, chave: string, estado: 'lido' | 'tratado') =>
  escrever<void>(sessao, '/api/validades/alertas/decisao', { chave, estado });

/* ------------------------------------------------------------------ */

/** '2026-10-05' → '05/10/2026'. */
export function data(d: string | null): string {
  if (!d) return 'não informada';
  return `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
}

/** '2026-10-05' → '05/10'. */
export const diaMes = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;

const ROTULO_DA_ORIGEM: Record<Lote['origem'], string> = {
  manual: 'Manual',
  relatorio: 'Relatório',
  migracao_1_0: 'InovaPay 1.0',
};
export const origem = (o: Lote['origem']) => ROTULO_DA_ORIGEM[o];

export type Tom = 'erro' | 'aviso' | 'ok' | 'neutro';

/** O estado do lote em texto (nunca só cor) e o tom do selo. */
export function rotuloDoEstado(
  e: EstadoDoLote,
  quantidadeDesconhecida = false,
): { texto: string; tom: Tom } {
  switch (e.estado) {
    case 'vencido':
      return { texto: 'Vencido', tom: 'erro' };
    case 'vencido_no_mes':
      return {
        texto: `Vencido há ${String(-e.dias)} ${-e.dias === 1 ? 'dia' : 'dias'}`,
        tom: 'erro',
      };
    case 'esgotado':
      return { texto: 'Esgotado', tom: 'neutro' };
    case 'sem_validade':
      return { texto: 'Validade não informada', tom: 'neutro' };
    case 'fora_da_janela':
      return { texto: 'Fora da janela', tom: 'ok' };
    case 'vence_em':
      if (quantidadeDesconhecida) return { texto: 'Saldo por lote indisponível', tom: 'neutro' };
      if (e.dias === 0) return { texto: 'Vence hoje', tom: 'erro' };
      return {
        texto: `Vence em ${String(e.dias)} ${e.dias === 1 ? 'dia' : 'dias'}`,
        tom: e.dias <= 30 ? 'aviso' : 'ok',
      };
  }
}

/* ------------------------------------------------------------------ *
 * Vencidos por mês — o histórico do que venceu (inclusive o que já saiu do programa)
 * ------------------------------------------------------------------ */

export type LoteVencidoNoMes = {
  readonly loteId: string;
  readonly productId: string;
  readonly descricao: string;
  readonly codigoProduto: string | null;
  readonly codigo: string | null;
  readonly validade: string;
  readonly vendidas: number;
  readonly sobraram: number | null;
  readonly valorACusto: string | null;
  readonly situacao: 'no_mes' | 'expirado' | 'desativado';
  readonly saiuEm: string | null;
  readonly motivo: string | null;
};
export type VencidosDoMes = {
  readonly mes: string;
  /** O intervalo de validade consultado (o mês inteiro, ou o período do filtro). */
  readonly de: string;
  readonly ate: string;
  readonly hoje: string;
  readonly meses: readonly { readonly mes: string; readonly lotes: number }[];
  readonly resumo: {
    readonly lotes: number;
    readonly produtos: number;
    readonly vendidas: number;
    readonly sobraram: number;
    readonly semSaldoInformado: number;
    readonly valorACusto: string | null;
    readonly semCusto: number;
  };
  readonly lotes: readonly LoteVencidoNoMes[];
};
export const lerVencidosDoMes = (mesOuPeriodo: string | { de: string; ate: string }) =>
  ler<VencidosDoMes>(
    typeof mesOuPeriodo === 'string'
      ? `/api/validades/vencidos?mes=${mesOuPeriodo}`
      : `/api/validades/vencidos?de=${mesOuPeriodo.de}&ate=${mesOuPeriodo.ate}`,
  );
